import { getBreadcrumbsObject } from "#src/Routers/breadcrumbsHelper";
import { useHistory, useNavigate, useParams } from "#src/Routers/hooks";
import { DevicesFilterPanel } from "#src/batteries-included-components/Panels/FilterPanels/DevicesFilterPanel";
import { SelectedDevicesPanel } from "#src/batteries-included-components/Panels/SelectedEntitiesPanels/SelectedDevicesPanel";
import { DevicesTablePanel } from "#src/batteries-included-components/Panels/TablePanels/DevicesTablePanel";
import { useClearDeviceCache } from "#src/components/hooks/adapters/useDevices";
import {
  EventCategoryContext,
  EventCategoryProvider,
} from "#src/contexts/EventCategoryContext";
import { EventContext, EventProvider } from "#src/contexts/EventContext";
import {
  EventTemplateContext,
  EventTemplateProvider,
} from "#src/contexts/EventTemplateContext";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { EVENTS_BREADCRUMB } from "#src/routes/events";
import { EVENT_CATEGORIES_BREADCRUMB } from "#src/routes/events/event-categories";
import { EVENT_CATEGORY_DETAIL_BREADCRUMB } from "#src/routes/events/event-categories/[eventCategoryId]";
import { EVENT_TEMPLATE_DETAIL_BREADCRUMB } from "#src/routes/events/event-categories/[eventCategoryId]/templates/[templateId]/details";
import {
  EVENT_DETAIL_BREADCRUMB,
  linkToEventDetails,
} from "#src/routes/events/event-categories/[eventCategoryId]/templates/[templateId]/events/[eventId]";
import { Button, Page } from "@validereinc/common-components";
import { EventAdapter } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useContext, useState } from "react";
import { ADD_DEVICES_BREADCRUMB, ADD_DEVICES_PAGE_TITLE } from "../";
import styles from "./AddDevicesPage.module.scss";

const cx = classNames.bind(styles);

export const AddDevicesToEventContent = () => {
  const urlParams = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const history = useHistory();
  const [filterConfigStorageKey, tableConfigStorageKey, tableStateStorageKey] =
    useStorageKey(
      ["filter-configuration", "table-configuration", "table-state"],
      "events-devices-add"
    );
  const { invalidate: invalidateDeviceCache } = useClearDeviceCache();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { eventCategory } = useContext(EventCategoryContext) || {};
  const { eventTemplate } = useContext(EventTemplateContext) || {};
  const { event } = useContext(EventContext) || {};

  const [selectedDevices, setSelectedDevices] = useState<any[]>([]);

  const breadcrumbs = getBreadcrumbsObject(
    [
      EVENTS_BREADCRUMB,
      EVENT_CATEGORIES_BREADCRUMB,
      EVENT_CATEGORY_DETAIL_BREADCRUMB,
      EVENT_TEMPLATE_DETAIL_BREADCRUMB,
      EVENT_DETAIL_BREADCRUMB,
      ADD_DEVICES_BREADCRUMB,
    ],
    urlParams,
    { 2: eventCategory?.name, 3: eventTemplate?.name, 4: event?.name }
  );

  const onSelectDevice = (device) => {
    setSelectedDevices([...selectedDevices, device]);
  };

  const onDeselectDevice = (deviceId) => {
    setSelectedDevices(selectedDevices.filter(({ id }) => id !== deviceId));
  };

  const onCancel = async () => {
    history.goBack();
  };

  const onSubmit = async () => {
    setIsSubmitting(true);

    await Promise.all(
      selectedDevices.map(({ id }) => {
        return EventAdapter.assets.createOne({
          eventId: event?.id ?? "",
          assetId: id,
        });
      })
    );

    invalidateDeviceCache();

    navigate({
      pathname: linkToEventDetails(
        eventCategory?.id,
        eventTemplate?.id,
        event?.id
      ),
    });

    setIsSubmitting(false);
  };

  return (
    <Page
      title={ADD_DEVICES_PAGE_TITLE}
      breadcrumbs={breadcrumbs}
      footer={
        <div className={cx("footer")}>
          <Button
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          <div style={{ display: "flex", flexDirection: "row", gap: 16 }}>
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={isSubmitting || !selectedDevices.length}
            >
              Add
            </Button>
          </div>
        </div>
      }
    >
      <DevicesFilterPanel filterConfigStorageKey={filterConfigStorageKey} />

      <div className={cx("container")}>
        <DevicesTablePanel
          filterConfigStorageKey={filterConfigStorageKey}
          tableConfigStorageKey={tableConfigStorageKey}
          tableStateStorageKey={tableStateStorageKey}
          selectedDevices={selectedDevices}
          onSelectDevice={onSelectDevice}
          onDeselectDevice={onDeselectDevice}
        />

        <SelectedDevicesPanel
          selectedDevices={selectedDevices}
          onDeselectDevice={onDeselectDevice}
        />
      </div>
    </Page>
  );
};

export const AddDevicesToEventPage = () => {
  const { eventCategoryId, eventTemplateId, eventId } = useParams<{
    eventCategoryId: string;
    eventTemplateId: string;
    eventId: string;
  }>();

  return (
    <EventCategoryProvider eventCategoryId={eventCategoryId}>
      <EventTemplateProvider eventTemplateId={eventTemplateId}>
        <EventProvider eventId={eventId}>
          <AddDevicesToEventContent />
        </EventProvider>
      </EventTemplateProvider>
    </EventCategoryProvider>
  );
};
